import { DateTime } from 'luxon';

import { FreightTrackingStatusId } from './freight-details-types';
import { parseFreightStatus } from './parse-freight-status';

import type { FreightTrackingEvent } from './freight-details-types';
import type { LtlShipment } from '../../types/ltl';

/** The four coarse lifecycle positions shared by the tracking stepper and the status badge. */
export const FreightTrackingStep = {
  PendingPickup: 0,
  InTransit: 1,
  OutForDelivery: 2,
  Delivered: 3,
} as const;
export type FreightTrackingStep = (typeof FreightTrackingStep)[keyof typeof FreightTrackingStep];

/**
 * Maps a FreightTrackingStatusId to a stepper index (0–3), in lifecycle order.
 *
 * Only statuses that describe *progress* appear here. `Unknown`, `Exception`, and `Voided`
 * describe state rather than position, so they are deliberately absent — a lookup miss is
 * what makes the caller fall through to event-based or coarse-status positioning instead of
 * pinning the stepper to a wrong step.
 */
const STEP_BY_STATUS_ID: Record<number, FreightTrackingStep> = {
  [FreightTrackingStatusId.Created]: FreightTrackingStep.PendingPickup,
  [FreightTrackingStatusId.PendingPickup]: FreightTrackingStep.PendingPickup,
  [FreightTrackingStatusId.Dispatched]: FreightTrackingStep.PendingPickup,
  [FreightTrackingStatusId.InRouteToPickup]: FreightTrackingStep.PendingPickup,
  [FreightTrackingStatusId.AtPickup]: FreightTrackingStep.PendingPickup,
  [FreightTrackingStatusId.InTransit]: FreightTrackingStep.InTransit,
  [FreightTrackingStatusId.OutForDelivery]: FreightTrackingStep.OutForDelivery,
  [FreightTrackingStatusId.AtDelivery]: FreightTrackingStep.OutForDelivery,
  [FreightTrackingStatusId.Delivered]: FreightTrackingStep.Delivered,
};

/**
 * Milliseconds for an event timestamp, or `-Infinity` when absent or unparseable.
 *
 * The zone is explicit because nothing guarantees a uniform serialized shape across sources —
 * offset-bearing and unsuffixed values both have to land on the same instant. Comparing the
 * raw strings would order them by shape rather than by time.
 */
export const eventTimestampMillis = (value: string | null | undefined): number => {
  const parsed = DateTime.fromISO(value ?? '', { zone: 'utc' });
  return parsed.isValid ? parsed.toMillis() : -Infinity;
};

/**
 * Step of the most recent event that carries a stepper position, or undefined when none do.
 *
 * Selects by instant rather than array position: the API applies no ordering to `events`,
 * so their order is whatever the carrier emitted. Ties break toward the further step.
 */
const stepFromNewestPositionalEvent = (events: FreightTrackingEvent[]): FreightTrackingStep | undefined => {
  const positioned = events.flatMap((event) => {
    const step = event.freightTrackingStatusId != null ? STEP_BY_STATUS_ID[event.freightTrackingStatusId] : undefined;
    return step !== undefined ? [{ step, at: eventTimestampMillis(event.eventDateTimeUtc) }] : [];
  });

  if (positioned.length === 0) return undefined;

  return positioned.reduce((newest, current) => {
    if (current.at > newest.at) return current;
    return current.at === newest.at && current.step > newest.step ? current : newest;
  }).step;
};

/** True when the shipment was voided or cancelled — an off-ladder terminal state. */
export const isShipmentCancelled = (shipment: LtlShipment): boolean =>
  shipment.freightTrackingStatusId === FreightTrackingStatusId.Voided || parseFreightStatus(shipment.status) === 'Cancelled';

/** True when the shipment is in an exception state, which describes *what* went wrong, not *where*. */
export const isShipmentException = (shipment: LtlShipment): boolean =>
  shipment.freightTrackingStatusId === FreightTrackingStatusId.Exception || parseFreightStatus(shipment.status) === 'Exception';

/**
 * Derives the active step from the shipment's `freightTrackingStatusId`, which is the authority
 * for where the shipment is. `events` exist for the Updates accordion and are read here for one
 * reason only: an `Exception` status says something went wrong but not where, and the entity
 * stores no furthest-reached status to recover the position from. Once the backend persists one,
 * this can drop the `events` argument entirely. Callers that render exceptions as their own
 * state rather than as a position on the ladder can pass an empty array.
 *
 * Falls back to the coarse `shipment.status` whenever the tracking status carries no position
 * (absent, `Unknown`, `Voided`, or an `Exception` with no positional events). An `Exception`
 * with no positional signal defaults to `InTransit` — exceptions occur while freight is in
 * motion, so Pending Pickup is always wrong. Events refine the position when available.
 */
export const activeTrackingStep = (shipment: LtlShipment, events: FreightTrackingEvent[]): FreightTrackingStep => {
  const { freightTrackingStatusId } = shipment;

  if (freightTrackingStatusId != null) {
    const step = STEP_BY_STATUS_ID[freightTrackingStatusId];
    if (step !== undefined) return step;
  }

  if (isShipmentException(shipment)) {
    const step = stepFromNewestPositionalEvent(events);
    if (step !== undefined) return step;
  }

  switch (parseFreightStatus(shipment.status)) {
    case 'InTransit':
      return FreightTrackingStep.InTransit;
    case 'Exception':
      return FreightTrackingStep.InTransit;
    case 'Delivered':
      return FreightTrackingStep.Delivered;
    default:
      return FreightTrackingStep.PendingPickup;
  }
};
